import React, { FC, MouseEvent, useCallback, useMemo, useState } from "react";
import { connect, Dispatch } from "react-redux";
import makeStyles from "@material-ui/core/styles/makeStyles";
import IconButton from "@material-ui/core/IconButton";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import { format } from "lib/globalize-util";
import cardGridViewActions from "actions/cart-grid-view";
import { IStore } from "reducers";
import { CartGridView } from "reducers/cart-grid-view";
import { SelectionWhiteList } from "helpers/selection-state";
import theme from "styles/theme";
import { DATATEST } from "constants/integration";
import ActionsToolbar from "components/actions-toolbar";
import app from "lib/app";
import { removeLineItems, removeProducts, updateWorkingOrder } from "actions/cart-legacy";
import { ErrorOutlineOutlined } from "@material-ui/icons";
import MediaConfirmation from "components/dialog/media-confirmation";

type IOwnProps = {};

type IStateProps = {
    viewport: string;
    gridViewSelectedRows: CartGridView["selectedRows"];
    gridViewSelectedItems: CartGridView["selectedItems"];
    gridViewExpandedSizes: CartGridView["expandedSizes"];
    isBulkOrder?: boolean;
    isPivotMode?: boolean;
};

type IDispatchProps = {
    showSelectedGridViewSizes(): void;
    hideSelectedGridViewSizes(): void;
    deselectAllGridViewItems(): void;
};

export const BulkActions: FC<IOwnProps & IStateProps & IDispatchProps> = ({
    viewport,
    gridViewSelectedRows,
    gridViewSelectedItems,
    gridViewExpandedSizes,
    showSelectedGridViewSizes,
    hideSelectedGridViewSizes,
    deselectAllGridViewItems,
    isBulkOrder,
    isPivotMode,
}) => {
    const classes = useStyles();
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement>(null);
    const handleMenuClose = useCallback(() => setMenuAnchor(null), []);
    const handleMenu = useCallback((event: MouseEvent<HTMLButtonElement>) => setMenuAnchor(event.currentTarget), []);

    const removeConfirmationMessage = useMemo<string>(
        () =>
            isBulkOrder
                ? format("Removing the selected product(s) will remove them from all locations in your order.")
                : format("Do you want to remove the selected products from the Working Order?"),
        [isBulkOrder],
    );

    const handleClose = useCallback(() => {
        deselectAllGridViewItems();
    }, [deselectAllGridViewItems]);

    const selectionState = useMemo(() => new SelectionWhiteList(gridViewSelectedRows), [gridViewSelectedRows]);

    const [hasShownSizes, hasHiddenSizes] = useMemo(() => {
        const selectedShownSizes = gridViewSelectedRows.intersect(gridViewExpandedSizes);
        return [selectedShownSizes.size > 0, gridViewSelectedRows.size > selectedShownSizes.size];
    }, [gridViewSelectedRows, gridViewExpandedSizes]);

    const handleShowClick = useCallback(() => {
        showSelectedGridViewSizes();
        setMenuAnchor(null);
        handleClose();
    }, [handleClose, showSelectedGridViewSizes]);
    const handleHideClick = useCallback(() => {
        hideSelectedGridViewSizes();
        setMenuAnchor(null);
        handleClose();
    }, [handleClose, hideSelectedGridViewSizes]);

    const handleBulkRemoveClick = useCallback(
        () => {
            setMenuAnchor(null);
            setModalOpen(true);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [handleClose],
    );

    const closeModal = useCallback(() => {
        setModalOpen(false);
    }, []);
    const submitModal = useCallback(() => {
        closeModal();
        deselectAllGridViewItems();
        if (isBulkOrder) {
            app.legacy.sendMessage(
                updateWorkingOrder(removeProducts(gridViewSelectedItems.map((item) => item.product))),
            );
        } else {
            app.legacy.sendMessage(updateWorkingOrder(removeLineItems(gridViewSelectedItems.map((item) => item._id))));
        }
    }, [closeModal, deselectAllGridViewItems, gridViewSelectedItems, isBulkOrder]);

    return (
        <ActionsToolbar
            selection={selectionState}
            viewport={viewport}
            onClose={handleClose}
            dataTest={DATATEST.SITE_HEADER.BULK_ACTIONS.CONTAINER}
        >
            <IconButton onClick={handleMenu} data-test={DATATEST.SITE_HEADER.BULK_ACTIONS.MENU_BTN}>
                <MoreVertIcon className={classes.icon} />
            </IconButton>
            <Menu key="bulk-upload-menu" anchorEl={menuAnchor} open={!!menuAnchor} onClose={handleMenuClose}>
                {!isPivotMode && (
                    <MenuItem
                        className={classes.menuItem}
                        onClick={handleShowClick}
                        disabled={!hasHiddenSizes}
                        data-test={DATATEST.SITE_HEADER.BULK_ACTIONS.SHOW}
                    >
                        {format("Show sizes")}
                    </MenuItem>
                )}
                {!isPivotMode && (
                    <MenuItem
                        className={classes.menuItem}
                        onClick={handleHideClick}
                        disabled={!hasShownSizes}
                        data-test={DATATEST.SITE_HEADER.BULK_ACTIONS.HIDE}
                    >
                        {format("Hide sizes")}
                    </MenuItem>
                )}
                <MenuItem
                    className={classes.menuItem}
                    onClick={handleBulkRemoveClick}
                    data-test={DATATEST.SITE_HEADER.BULK_ACTIONS.REMOVE}
                >
                    {format("Remove selected products")}
                </MenuItem>
            </Menu>

            <MediaConfirmation
                open={modalOpen}
                title={
                    <div className={classes.title}>
                        <ErrorOutlineOutlined fontSize="large" htmlColor={theme.palette.error} />
                        <span>{format("Remove products?")}</span>
                    </div>
                }
                disablePortal={true}
                saveText={format("Remove products")}
                closeText={format("Cancel")}
                onClose={closeModal}
                onSave={submitModal}
            >
                {removeConfirmationMessage}
            </MediaConfirmation>
        </ActionsToolbar>
    );
};

const useStyles = makeStyles({
    menuItem: {
        fontSize: 15,
        fontFamily: "inherit",
        letterSpacing: "inherit",
        minWidth: "12rem",
    },
    icon: {
        width: 24,
        height: 24,
        color: theme.palette.darkerGrey,
    },
    title: {
        fontSize: "20px",
        fontsize: 400,
        color: theme.palette.textColor,
        display: "flex",
        alignItems: "center",
        lineHeight: "24px",
        "& span": {
            marginLeft: "12px",
        },
    },
});

const mapStateToProps = (state: IStore): IStateProps => ({
    gridViewSelectedRows: state.cartGridView.selectedRows,
    gridViewSelectedItems: state.cartGridView.selectedItems,
    gridViewExpandedSizes: state.cartGridView.expandedSizes,
    isPivotMode: !!state.cartGridView.gridState?.pivot?.mode,
    isBulkOrder: state.cart?.isBulkOrder,
    viewport: state.app.viewport,
});

const mapDispatchToProps = (dispatch: Dispatch<{}>): IDispatchProps => ({
    showSelectedGridViewSizes: () => dispatch(cardGridViewActions.showSelectedSizes(true)),
    hideSelectedGridViewSizes: () => dispatch(cardGridViewActions.hideSelectedSizes(true)),
    deselectAllGridViewItems: () => dispatch(cardGridViewActions.deselectAll(true)),
});

export default connect<IStateProps, IDispatchProps, IOwnProps>(mapStateToProps, mapDispatchToProps)(BulkActions);
